// Module 7 — Bordereaux & Accounting.
// Route: /reinsurance/accounting

import React from 'react';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import AccountDrawer from './AccountDrawer';
import {
  C, Icons, Kpi, Pill, TwoLine, PrimaryButton, GhostButton, money0, moneyMixed, date, } from './kit';

// "USD 761,250.00 / due to us" — the direction is the point. An amount with
// no recipient is not an answer to "who owes what".
const dueLabel = (a) => (a.balance?.dueTo === 'us' ? 'due to us'
  : a.balance?.dueTo === 'counterparty' ? 'due to market' : 'nil');

// One account per participating market on the chosen treaty, for one period.
// POST /reinsurance/accounts/generate refuses a treaty with no markets, and
// returns the accounts already there when the period has been generated
// before, so pressing it twice is safe.
const generateForm = (t) => ({
  title: t('screens.reAccounting.genTitle', 'Generate accounts from a treaty'),
  subtitle: t('screens.reAccounting.genSub', 'One technical account per participating market, for one period'),
  submitLabel: t('screens.reAccounting.genBtn', 'Generate'),
  endpoint: '/reinsurance/accounts/generate',
  fields: [
    { key: 'treaty', label: t('screens.reAccounting.fTreatyIn', 'Treaty'), type: 'lookup', required: true,
      endpoint: '/reinsurance/treaties',
      optionLabel: (r) => [r.treatyNumber, r.name || r.cedantName].filter(Boolean).join(' — '),
      placeholder: t('screens.reAccounting.fTreatyPick', 'Choose the treaty…'),
      emptyLabel: t('screens.reAccounting.fTreatyNone', 'No treaties yet'),
      emptyHelp: t('screens.reAccounting.fTreatyNoneHelp', 'Accounts are generated against a treaty and its signed markets.') },
    { key: 'periodFrom', label: t('screens.reAccounting.fFromIn', 'Period from'), type: 'date', required: true },
    { key: 'periodTo', label: t('screens.reAccounting.fToIn', 'Period to'), type: 'date', required: true },
    { key: 'dueDate', label: t('screens.reAccounting.fDueIn', 'Due date'), type: 'date',
      help: t('screens.reAccounting.fDueHelp', 'Leave blank for the market default of 30 days.') },
  ],
});

export default function AccountsList() {
  const { t } = useTranslation();

  const columns = [
    { key: 'account', label: t('screens.reAccounting.colNumber', 'Account'), width: 160,
      render: (r) => <TwoLine primary={r.accountNumber} secondary={r.treatyNumber} /> },
    { key: 'treatyRef', label: t('screens.reAccounting.colTreatyRef', 'Treaty ref'),
      render: (r) => r.treatyNumber || '—' },
    { key: 'counterpartyName', label: t('screens.reAccounting.colCounterparty', 'Counterparty'),
      render: (r) => r.counterpartyName || '—' },
    { key: 'period', label: t('screens.reAccounting.colPeriod', 'Period'),
      render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{date(r.periodFrom)} – {date(r.periodTo)}</span> },
    { key: 'balance', label: t('screens.reAccounting.colBalance', 'Balance'), numeric: true,
      render: (r) => (
        <span>
          <span style={{ display: 'block', fontWeight: 600, color: C.ink }}>
            {money0(Math.abs(r.balance?.net ?? 0), r.currency)}
          </span>
          <span style={{ display: 'block', fontSize: 11.5, color: C.muted }}>{dueLabel(r)}</span>
        </span>
      ) },
    { key: 'outstanding', label: t('screens.reAccounting.colOutstanding', 'Outstanding'), numeric: true,
      render: (r) => money0(Math.abs(r.outstanding ?? 0), r.currency) },
    { key: 'daysOverdue', label: t('screens.reAccounting.colOverdue', 'Overdue'), numeric: true, width: 84,
      render: (r) => (Number(r.daysOverdue) > 0
        ? <span style={{ color: C.red, fontWeight: 700 }}>{r.daysOverdue}d</span>
        : <span style={{ color: C.faint }}>—</span>) },
    { key: 'status', label: t('screens.reAccounting.colStatus', 'Status'), width: 96,
      render: (r) => <Pill status={r.status} /> },
  ];

  return (
    <ModulePage
      title={t('screens.reAccounting.title', 'Bordereaux & Accounting')}
      subtitle={t('screens.reAccounting.subtitle', 'Technical accounts, statements and settlement')}
      endpoint='/reinsurance/accounts'
      searchPlaceholder={t('screens.reAccounting.search', 'Search accounts, counterparties or treaty references…')}
      createForm={generateForm(t)}
      actions={({ openCreate }) => (
        <>
          {/* "Generate from treaty" rendered and did nothing when clicked —
              no handler was attached, so the whole treaty-to-account route
              was unreachable from the screen that needs it. It opens the
              form now; the endpoint raises one account per participating
              market and is idempotent for a period already generated.
              "Import bordereaux" is a file-upload flow that does not exist
              yet, so it is not offered rather than offered and dead. */}
          <PrimaryButton onClick={openCreate}>
            {t('screens.reAccounting.generate', 'Generate from treaty')}
          </PrimaryButton>
        </>
      )}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.db()} tone='blue'
            value={moneyMixed(s?.outstandingBalances)} trend={s?.outstandingBalances?.trend}
            comparison={s?.outstandingBalances?.comparison}
            label={t('screens.reAccounting.kpiOutstanding', 'Outstanding balances')} />
          <Kpi icon={Icons.doc()} tone='green'
            value={s?.issuedStatements?.value ?? '—'} trend={s?.issuedStatements?.trend}
            comparison={s?.issuedStatements?.comparison}
            label={t('screens.reAccounting.kpiIssued', 'Issued statements')} />
          <Kpi icon={Icons.alert()} tone='red'
            value={s?.overdueAccounts?.value ?? '—'} trend={s?.overdueAccounts?.trend}
            comparison={s?.overdueAccounts?.comparison}
            label={t('screens.reAccounting.kpiOverdue', 'Overdue accounts')} />
          <Kpi icon={Icons.clock()} tone='amber'
            value={s?.reconciliationsPending?.value ?? '—'} trend={s?.reconciliationsPending?.trend}
            comparison={s?.reconciliationsPending?.comparison}
            label={t('screens.reAccounting.kpiRecon', 'Reconciliations pending')} />
        </>
      )}
      filters={[
        { key: 'period', type: 'daterange', label: 'Period' },
        { key: 'status', label: 'Status', options: [
          { value: 'all', label: 'All statuses' },
          { value: 'draft', label: 'Draft' },
          { value: 'issued', label: 'Issued' },
          { value: 'agreed', label: 'Agreed' },
          { value: 'settled', label: 'Settled' },
          { value: 'disputed', label: 'Disputed' },
        ] },
        { key: 'overdue', label: 'View', options: [
          { value: 'all', label: 'All accounts' },
          { value: 'true', label: 'Overdue only' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.reAccounting.empty', 'No technical accounts. Generate a set from a treaty and period to begin.')}
      drawerTabs={['Overview', 'Line Items', 'Settlement', 'Documents', 'Activity']}
      drawerHeader={(r) => ({
        title: r.accountNumber,
        badge: <Pill status={r.status} />,
        lines: [r.counterpartyName, ['Treaty', r.treatyNumber].filter(Boolean).join(' · ')].filter(Boolean),
      })}
      drawer={(r, { tab, reload }) => (
        <AccountDrawer row={r} tab={tab} reload={reload} />
      )}
    />
  );
}
